import type { Tone } from '@nexvia-logistics/ui';

import { ecommerceImportsEnabled } from 'shared/config/ecommerce';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  createdAt?: string;
  readAt?: string | null;
  referenceId?: string | null;
}

/**
 * What each kind of notification is about, in the design system's vocabulary.
 *
 * The package deliberately does not know what a notification means: it takes a `tone` from
 * the same closed set `Chip` uses, and this table is where the six kinds this app sends are
 * mapped onto it. The version this replaces carried six literal hex pairs read off MUI's
 * computed palette, with a comment recording that none of them had a token match.
 *
 * Three of the six turned out to be byte-identical to a tone background - `quote_received`
 * to `positive` (#E8F5E9) and both expiry reminders to `caution` (#FFF3E0). The other three
 * are a judgement: `in_transit` was a solid mid-blue with a dark blue glyph on it, and
 * `new_question` was MUI's stock `secondary` purple, which arrived because `nexviaTheme`
 * never defines `secondary` - nobody chose it.
 */
export const NOTIFICATION_TONES: Record<string, Tone> = {
  quote_received: 'positive',
  expiration_reminder_72h: 'caution',
  expiration_reminder_48h: 'caution',
  expiration_reminder_24h: 'critical',
  in_transit: 'info',
  new_question: 'active',
  // DEC-48: the four refund notices the client gets (SPEC-REEMBOLSO-MANUAL §7, W5).
  refund_pending_review: 'caution',
  refund_processed: 'positive',
  refund_processed_adjusted: 'positive',
  refund_rejected: 'critical',
  // NEX-1380: a store purchase waiting for its buyer - on arrival, on its last day and once expired.
  purchase_import_awaiting_buyer: 'caution',
  purchase_import_reminder: 'critical',
  purchase_import_expired: 'critical',
};

/**
 * Where a notification takes you. Only the ones that carry a shipment go somewhere, or a store
 * purchase: its notices carry the purchase import as `referenceId`, and open it while the
 * purchases page is switched on.
 */
export function notificationHref(notification: AppNotification): string | null {
  const id = notification.referenceId;
  if (!id) return null;
  switch (notification.type) {
    case 'purchase_import_awaiting_buyer':
    case 'purchase_import_reminder':
    case 'purchase_import_expired':
      return ecommerceImportsEnabled ? `/purchases/${id}` : '/shipments';
    case 'new_question':
    case 'quote_received':
    case 'expiration_reminder_72h':
    case 'expiration_reminder_48h':
    case 'expiration_reminder_24h':
    case 'in_transit':
    case 'refund_pending_review':
    case 'refund_processed':
    case 'refund_processed_adjusted':
    case 'refund_rejected':
      return `/shipments/${id}/quotes`;
    default:
      return '/shipments';
  }
}
